import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Header from '@/components/Header';
import { DELIVERY_OPTIONS, deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterAdvertiser = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [nextAdvertiserId, setNextAdvertiserId] = useState('');
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    initials: '',
    designation: '',
    company_name: '',
    contact_no: '',
    whatsapp_no: '',
    email: '',
    address: '',
    pincode: '',
    state: '',
    district: '',
    gst_no: '',
    po_wo_no: '',
    size: '',
    duration: '',
    amount: '',
    payment_status: '',
    status: 'Active',
    start_date: '',
    end_date: '',
    delivery_mode: '',
    notes: '',
    print_schedule_type: '',
    schedule_type: '',
    magazine: ''
  });

  const [initials, setInitials] = useState([]);

  // Auto-populate WhatsApp number from contact number
  const handleContactChange = (value) => {
    setFormData(prev => {
      const newData = { ...prev, contact_no: value };
      // Auto-populate WhatsApp number if empty
      if (!prev.whatsapp_no || prev.whatsapp_no === '') {
        const digits = value.replace(/\D/g, '');
        if (digits.length >= 10) {
          newData.whatsapp_no = digits.slice(-10);
        }
      }
      return newData;
    });
  };

  useEffect(() => {
    fetchStates();
    fetchInitials();
  }, []);

  useEffect(() => {
    if (formData.magazine) {
      fetchNextAdvertiserId();
    }
  }, [formData.magazine]);

  const fetchNextAdvertiserId = async () => {
    try {
      const response = await axios.get(`${API}/advertisers/next-id`, {
        params: { magazine: formData.magazine }
      });
      setNextAdvertiserId(response.data.next_id);
    } catch (error) {
      console.error('Error fetching next advertiser ID:', error);
      toast.error('Failed to fetch next advertiser ID');
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/states`);
      setStates(response.data.states);
    } catch (error) {
      console.error('Error fetching states:', error);
      toast.error('Failed to fetch states');
    }
  };

  const fetchInitials = async () => {
    try {
      const response = await axios.get(`${API}/initials`);
      setInitials(response.data.initials || []);
    } catch (error) {
      console.error('Error fetching initials:', error);
      // Use default initials if fetch fails
      setInitials(["Dr.", "M/s", "M/s.", "Mr", "Mr.", "Mrs", "Mrs.", "Ms.", "Prof.", "Shri", "Shri.", "Smt.", "Sri.", "The"]);
    }
  };

  const fetchDistricts = async (state) => {
    if (!state) {
      setDistricts([]);
      return;
    }
    try {
      const response = await axios.post(`${API}/advertisers/unique-districts`, { state: state });
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  // Fetch districts when state changes
  useEffect(() => {
    if (formData.state) {
      fetchDistricts(formData.state);
    } else {
      setDistricts([]);
    }
  }, [formData.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.magazine || !formData.company_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/advertisers`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(`Advertiser registered successfully with ID: ${nextAdvertiserId}`);
      
      // Reset form
      setFormData({
        name: '',
        initials: '',
        designation: '',
        company_name: '',
        contact_no: '',
        email: '',
        address: '',
        pincode: '',
        state: '',
        district: '',
        gst_no: '',
        po_wo_no: '',
        size: '',
        duration: '',
        amount: '',
        payment_status: '',
        status: 'Active',
        start_date: '',
        end_date: '',
        delivery_mode: '',
        notes: '',
        print_schedule_type: '',
        schedule_type: '',
        magazine: ''
      });
      setNextAdvertiserId('');
    } catch (error) {
      console.error('Error registering advertiser:', error);
      toast.error('Failed to register advertiser');
    } finally {
      setLoading(false);
    }
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const years = Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() + i).toString());

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
              <h1 className="text-4xl font-bold" style={{ color: 'var(--primary-blue)' }} data-testid="register-title">
                Register Advertiser
              </h1>
            </div>
            {nextAdvertiserId && (
              <div className="text-xl font-semibold" style={{ color: 'var(--primary-blue)' }}>
                Next ID: {nextAdvertiserId}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="form-section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Magazine *</Label>
                <Select value={formData.magazine} onValueChange={(value) => setFormData({ ...formData, magazine: value })}>
                  <SelectTrigger data-testid="magazine-select">
                    <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Initials</Label>
              <Select value={formData.initials} onValueChange={(value) => setFormData({ ...formData, initials: value })}>
                <SelectTrigger data-testid="initials-select">
                  <SelectValue placeholder="Select initials" />
                </SelectTrigger>
                <SelectContent>
                  {initials.map((init) => (
                    <SelectItem key={init} value={init}>{init}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Name *</Label>
              <Input data-testid="name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter name" required />
            </div>

            <div className="space-y-2">
              <Label>Designation</Label>
              <Input data-testid="designation-input" value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} placeholder="Enter designation" />
            </div>

            <div className="space-y-2">
              <Label>Company *</Label>
              <Input data-testid="company-input" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder="Enter company name" required />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Address *</Label>
              <Textarea data-testid="address-input" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Fill up address without District name and pincode" rows={3} />
            </div>

            <div className="space-y-2">
              <Label>District *</Label>
              <Input 
                data-testid="district-input" 
                list="district-options-adv"
                value={formData.district} 
                onChange={(e) => setFormData({ ...formData, district: e.target.value })} 
                placeholder={formData.state ? "Select or type district" : "Select state first"}
                disabled={!formData.state}
              />
              <datalist id="district-options-adv">
                {districts.map(district => (
                  <option key={district} value={district} />
                ))}
              </datalist>
            </div>

            <div className="space-y-2">
              <Label>Pincode (6 digits) *</Label>
              <Input 
                data-testid="pincode-input" 
                type="text"
                maxLength={6}
                value={formData.pincode} 
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                  setFormData({ ...formData, pincode: value });
                }} 
                placeholder="Enter 6 digit pincode" 
              />
            </div>

            <div className="space-y-2">
              <Label>State</Label>
              <Select value={formData.state} onValueChange={(value) => {
                setFormData({ ...formData, state: value, district: '' });
                fetchDistricts(value);
              }}>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Size</Label>
              <Select value={formData.size} onValueChange={(value) => setFormData({ ...formData, size: value })}>
                <SelectTrigger data-testid="size-select">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Full Page">Full Page</SelectItem>
                  <SelectItem value="Half Page">Half Page</SelectItem>
                  <SelectItem value="Quarter Page">Quarter Page</SelectItem>
                  <SelectItem value="Custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Advt. Duration</Label>
              <Select value={formData.duration} onValueChange={(value) => setFormData({ ...formData, duration: value })}>
                <SelectTrigger data-testid="duration-select">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1 Month">1 Month</SelectItem>
                  <SelectItem value="3 Months">3 Months</SelectItem>
                  <SelectItem value="6 Months">6 Months</SelectItem>
                  <SelectItem value="1 Year">1 Year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Amount</Label>
              <Input data-testid="amount-input" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="Enter amount" />
            </div>

            <div className="space-y-2">
              <Label>Payment Status</Label>
              <Select value={formData.payment_status} onValueChange={(value) => setFormData({ ...formData, payment_status: value })}>
                <SelectTrigger data-testid="payment-status-select">
                  <SelectValue placeholder="Select payment status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Partial">Partial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="text" data-testid="email-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Enter email (multiple emails separated by comma)" required />
              <p className="text-xs text-gray-500">You can enter multiple emails separated by comma. Example: email1@example.com, email2@example.com</p>
            </div>

            <div className="space-y-2">
              <Label>Contact</Label>
              <Input data-testid="contact-input" value={formData.contact_no} onChange={(e) => handleContactChange(e.target.value)} placeholder="Enter contact number" />
            </div>

            <div className="space-y-2">
              <Label>WhatsApp Number</Label>
              <Input 
                data-testid="whatsapp-input" 
                value={formData.whatsapp_no} 
                onChange={(e) => setFormData({ ...formData, whatsapp_no: e.target.value.replace(/\D/g, '').slice(-10) })} 
                placeholder="10-digit WhatsApp number"
                maxLength={10}
              />
              <p className="text-xs text-gray-500">Auto-filled from contact. +91 assumed.</p>
            </div>

            <div className="space-y-2">
              <Label>GST No</Label>
              <Input 
                data-testid="gst-no-input" 
                value={formData.gst_no} 
                onChange={(e) => setFormData({ ...formData, gst_no: e.target.value })} 
                placeholder="Enter GST number" 
              />
            </div>

            <div className="space-y-2">
              <Label>PO/WO No</Label>
              <Input 
                data-testid="po-wo-no-input" 
                value={formData.po_wo_no} 
                onChange={(e) => setFormData({ ...formData, po_wo_no: e.target.value })} 
                placeholder="Enter PO/WO number" 
              />
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input 
                type="date" 
                data-testid="start-date-input" 
                value={formData.start_date} 
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} 
              />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input 
                type="date" 
                data-testid="end-date-input" 
                value={formData.end_date} 
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} 
              />
            </div>

            <div className="space-y-2">
              <Label>Delivery Mode</Label>
              <Select value={deliveryLabel(formData.delivery_mode)} onValueChange={(value) => setFormData({ ...formData, delivery_mode: value })}>
                <SelectTrigger data-testid="delivery-mode-select">
                  <SelectValue placeholder="Select delivery mode" />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Print Schedule Type</Label>
              <Select value={formData.print_schedule_type} onValueChange={(value) => setFormData({ ...formData, print_schedule_type: value })}>
                <SelectTrigger data-testid="print-schedule-select">
                  <SelectValue placeholder="Select schedule" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                  <SelectItem value="Quarterly">Quarterly</SelectItem>
                  <SelectItem value="Custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Schedule Type</Label>
              <Select value={formData.schedule_type} onValueChange={(value) => setFormData({ ...formData, schedule_type: value })}>
                <SelectTrigger data-testid="schedule-type-select">
                  <SelectValue placeholder="Select schedule type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Regular">Regular</SelectItem>
                  <SelectItem value="Alternate">Alternate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Textarea data-testid="notes-input" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Enter any additional notes" rows={3} />
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button type="submit" disabled={loading} className="btn-primary" data-testid="submit-button">
              <Save className="mr-2" size={18} />
              {loading ? 'Registering...' : 'Register Advertiser'}
            </Button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default RegisterAdvertiser;